import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useAccount, useReadContract, useWriteContract, usePublicClient } from 'wagmi';
import { bsc } from 'wagmi/chains';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { parseAbi, formatUnits, parseEventLogs } from 'viem';
import axiosInstance from '../../api/axios';
import { toast } from 'react-toastify';
import { usePermissions } from '../../hooks/usePermissions';
import Pagination from '../../components/Pagination';
import SortableTableHeader from '../../components/SortableTableHeader';
import Web3Provider from '../../components/Web3Provider';

const CONTRACT_ADDRESS = '0x9754A9A3290c99EA1623ccC97B7755C39803D157';
const USDT_ADDRESS = '0x55d398326f99059fF775485246999027B3197955';

const SWEEP_ABI = parseAbi([
  'function sweep(bytes32 salt, address token, address to) returns (address depositAddress, uint256 amount)',
  'function batchSweep(bytes32[] salts, address token, address to) returns (uint256 totalAmount, uint256 sweptCount)',
  'function owner() view returns (address)',
  'event Swept(bytes32 indexed salt, address indexed depositAddress, address indexed to, address token, uint256 amount)',
]);

const ERC20_ABI = parseAbi([
  'function balanceOf(address account) view returns (uint256)',
]);

const TABS = ['Pending', 'History'];

const defaultFilters = { page: 1, pageSize: 15, username: '', startDate: '', endDate: '', order: 'desc' };
const defaultPagination = { currentPage: 1, totalPages: 1, totalRows: 0 };

// Extract a clean, human-readable message from a viem/wagmi error.
const parseWeb3Error = (err) => {
  if (!err) return 'Transaction failed';
  if (err.shortMessage) return err.shortMessage;
  if (err.details) return err.details;
  if (err.message) return String(err.message).split('\n')[0];
  return 'Transaction failed';
};

// ── Shared helpers ─────────────────────────────────────────────────────────────
const TxHashLink = ({ hash }) => {
  if (!hash) return <span className="text-muted-foreground/50">—</span>;
  return (
    <a
      href={`https://bscscan.com/tx/${hash}`}
      target="_blank"
      rel="noopener noreferrer"
      title={hash}
      className="font-mono text-xs text-ginox-green hover:underline"
    >
      {hash.slice(0, 8)}...{hash.slice(-6)}
    </a>
  );
};

const WalletCell = ({ address }) => {
  if (!address) return <span className="text-muted-foreground/50">—</span>;
  return (
    <span title={address} className="font-mono text-xs text-foreground">
      {address.slice(0, 8)}...{address.slice(-6)}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const isSuccess = status === 'success' || status === 'swept';
  return isSuccess ? (
    <span className="inline-flex items-center rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-400">
      {status || 'swept'}
    </span>
  ) : (
    <span className="inline-flex items-center rounded-full border border-yellow-500/30 bg-yellow-500/10 px-2 py-0.5 text-[10px] font-medium text-yellow-400">
      {status || 'pending'}
    </span>
  );
};

// ── History Tab ───────────────────────────────────────────────────────────────
const HistoryTab = ({ token }) => {
  const [filters, setFilters] = useState(defaultFilters);
  const [tempFilters, setTempFilters] = useState(defaultFilters);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState(defaultPagination);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ key: '', direction: 'asc' });
  const [sortedData, setSortedData] = useState([]);
  const [totals, setTotals] = useState({ all: 0, page: 0 });

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: filters.page, pageSize: filters.pageSize, order: filters.order };
      if (filters.username) params.username = filters.username;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      const res = await axiosInstance.get('/api/admin/sweep/history', { headers: { token }, params });
      if (res.data.success) {
        const d = res.data.data;
        const rows = d.sweeps || d.deposits || d.data || [];
        setData(rows);
        setSortedData(rows);
        setPagination({ currentPage: parseInt(d.page) || 1, totalPages: d.totalPages || 1, totalRows: d.total || 0 });
        setTotals({ all: d.totalReceivedAmount || 0, page: d.pageReceivedAmount || 0 });
      }
    } catch { toast.error('Failed to fetch sweep history'); }
    finally { setLoading(false); }
  }, [token, filters]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  useEffect(() => {
    if (!data.length) { setSortedData([]); return; }
    const sorted = [...data].sort((a, b) => {
      if (!sort.key) return 0;
      let av = a[sort.key] ?? '', bv = b[sort.key] ?? '';
      if (['amount', 'receivedAmount', 'gasFee'].includes(sort.key)) {
        av = parseFloat(av) || 0; bv = parseFloat(bv) || 0;
        return sort.direction === 'asc' ? av - bv : bv - av;
      }
      if (['createdAt', 'updatedAt'].includes(sort.key)) {
        av = new Date(av).getTime(); bv = new Date(bv).getTime();
        return sort.direction === 'asc' ? av - bv : bv - av;
      }
      av = String(av).toLowerCase(); bv = String(bv).toLowerCase();
      return sort.direction === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    setSortedData(sorted);
  }, [data, sort]);

  const applyFilters = () => setFilters({ ...tempFilters, page: 1 });
  const clearFilters = () => { setFilters(defaultFilters); setTempFilters(defaultFilters); };
  const handleSort = (key) => setSort(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
  const handlePageChange = (p) => { setFilters(prev => ({ ...prev, page: p })); setTempFilters(prev => ({ ...prev, page: p })); };

  return (
    <div>
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="rounded-lg border border-border/70 bg-card p-5">
          <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-2">Total Received (All Deposits)</p>
          <p className="text-3xl font-bold text-ginox-green">
            {loading ? '—' : Number(totals.all).toFixed(2)}{' '}
            <span className="text-base font-normal text-muted-foreground">USDT</span>
          </p>
        </div>
        <div className="rounded-lg border border-border/70 bg-card p-5">
          <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-2">Received (This Page)</p>
          <p className="text-3xl font-bold text-emerald-400">
            {loading ? '—' : Number(totals.page).toFixed(2)}{' '}
            <span className="text-base font-normal text-muted-foreground">USDT</span>
          </p>
        </div>
      </div>

      <div className="mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">Username / WalletAddress</label>
            <input
              type="text"
              value={tempFilters.username}
              onChange={e => setTempFilters(p => ({ ...p, username: e.target.value }))}
              placeholder="Search…"
              className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            />
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">Start Date</label>
            <input
              type="date"
              value={tempFilters.startDate}
              onChange={e => setTempFilters(p => ({ ...p, startDate: e.target.value }))}
              className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            />
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">End Date</label>
            <input
              type="date"
              value={tempFilters.endDate}
              onChange={e => setTempFilters(p => ({ ...p, endDate: e.target.value }))}
              className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            />
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">Page Size</label>
            <select
              value={tempFilters.pageSize}
              onChange={e => setTempFilters(p => ({ ...p, pageSize: parseInt(e.target.value) }))}
              className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            >
              {[10, 15, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <button onClick={applyFilters} className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors">
            Apply Filters
          </button>
          <button onClick={clearFilters} className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors">
            Clear Filters
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        {loading ? (
          <p className="py-12 text-center text-sm text-muted-foreground">Loading...</p>
        ) : (
          <>
            <table className="w-full border-separate border-spacing-0">
              <thead>
                <tr>
                  {[['tranx_id', 'Tranx ID'], ['username', 'Username'], ['fromWallet', 'From Wallet'], ['toWallet', 'To Wallet'],
                    ['amount', 'Amount'], ['receivedAmount', 'Received'], ['gasFee', 'Gas Fee'], ['txHash', 'Tx Hash'], ['status', 'Status'], ['createdAt', 'Created At']]
                    .map(([key, label]) => (
                      <SortableTableHeader
                        key={key}
                        sortKey={key}
                        currentSort={sort}
                        onSort={handleSort}
                        className="border-b border-border/40 px-5 py-3.5 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground"
                      >
                        {label}
                      </SortableTableHeader>
                    ))}
                </tr>
              </thead>
              <tbody>
                {sortedData.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="border-b border-border/40 px-5 py-3.5 text-sm text-center text-muted-foreground">
                      No records found
                    </td>
                  </tr>
                ) : sortedData.map(row => (
                  <tr key={row._id} className="group hover:bg-white/[0.02] transition-colors">
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{row.tranx_id}</td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{row.username}</td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground"><WalletCell address={row.fromWallet} /></td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground"><WalletCell address={row.toWallet} /></td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{row.amount}</td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{row.receivedAmount}</td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{row.gasFee}</td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground"><TxHashLink hash={row.txHash} /></td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground"><StatusBadge status={row.status} /></td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{new Date(row.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
      <div className="border-t border-white/[0.08] px-4">
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalRows={pagination.totalRows}
          pageSize={filters.pageSize}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

// ── Pending Tab ───────────────────────────────────────────────────────────────
const PendingTab = ({ token }) => {
  const { address, isConnected } = useAccount();
  const publicClient = usePublicClient();
  const { writeContractAsync } = useWriteContract();

  // Contract owner — read straight from BSC regardless of wallet chain.
  const { data: contractOwner } = useReadContract({
    address: CONTRACT_ADDRESS,
    abi: SWEEP_ABI,
    functionName: 'owner',
    chainId: bsc.id,
  });
  const isOwner = useMemo(
    () => !!contractOwner && !!address && contractOwner.toLowerCase() === address.toLowerCase(),
    [contractOwner, address]
  );

  const [toAddress, setToAddress] = useState('');
  const [filters, setFilters] = useState(defaultFilters);
  const [tempFilters, setTempFilters] = useState(defaultFilters);
  const [data, setData] = useState([]);
  const [balances, setBalances] = useState({});
  const [pagination, setPagination] = useState(defaultPagination);
  const [stats, setStats] = useState({ totalDeposits: 0, totalUSDT: 0 });
  const [loading, setLoading] = useState(true);
  const [loadingBalances, setLoadingBalances] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [sweepingId, setSweepingId] = useState(null);
  const [isBatchSweeping, setIsBatchSweeping] = useState(false);
  const intervalRef = useRef(null);

  const fetchPending = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const params = { page: filters.page, pageSize: filters.pageSize, order: filters.order };
      if (filters.username) params.username = filters.username;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      const res = await axiosInstance.get('/api/admin/sweep/pending', { headers: { token }, params });
      if (res.data.success) {
        const d = res.data.data;
        const rows = d.deposits || [];
        setData(rows);
        setStats({ totalDeposits: d.totalDeposits || 0, totalUSDT: d.totalUSDT || 0 });
        setPagination({ currentPage: d.currentPage || 1, totalPages: d.totalPages || 1, totalRows: d.totalDeposits || 0 });
        return rows;
      }
      return [];
    } catch { if (!silent) toast.error('Failed to fetch pending deposits'); return []; }
    finally { if (!silent) setLoading(false); }
  }, [token, filters]);

  // Fetch USDT on-chain balances via a single Multicall3 call (viem auto-uses it).
  const fetchBalances = useCallback(async (deposits) => {
    if (!deposits || deposits.length === 0 || !publicClient) return;
    setLoadingBalances(true);
    try {
      // Only query well-formed addresses; malformed ones are marked unavailable.
      const queryable = deposits.filter(d => /^0x[0-9a-fA-F]{40}$/.test(d.toWalletAddress || ''));
      const results = await publicClient.multicall({
        allowFailure: true,
        contracts: queryable.map(d => ({
          address: USDT_ADDRESS,
          abi: ERC20_ABI,
          functionName: 'balanceOf',
          args: [d.toWalletAddress],
        })),
      });
      const byDeposit = {};
      queryable.forEach((d, i) => {
        const r = results[i];
        byDeposit[d._id] = r && r.status === 'success'
          ? parseFloat(formatUnits(r.result, 18))
          : null;
      });
      const map = {};
      deposits.forEach(d => { map[d._id] = byDeposit[d._id] ?? null; });
      setBalances(map);
    } catch {
      const map = {};
      deposits.forEach(d => { map[d._id] = null; });
      setBalances(map);
    } finally {
      setLoadingBalances(false);
    }
  }, [publicClient]);

  useEffect(() => {
    const load = async () => {
      const rows = await fetchPending();
      await fetchBalances(rows);
    };
    load();
    intervalRef.current = setInterval(async () => {
      const rows = await fetchPending(true);
      await fetchBalances(rows);
    }, 30000);
    return () => clearInterval(intervalRef.current);
  }, [fetchPending, fetchBalances]);

  const totalOnChain = useMemo(() =>
    Object.values(balances).reduce((s, v) => s + (v || 0), 0),
    [balances]
  );

  const validateSweep = () => {
    if (!isConnected || !address) { toast.error('Please connect your wallet first'); return false; }
    if (!isOwner) { toast.error('Connected wallet is not the contract owner'); return false; }
    if (!toAddress.trim()) { toast.error('Please enter a destination address'); return false; }
    if (!/^0x[0-9a-fA-F]{40}$/.test(toAddress.trim())) { toast.error('Invalid destination address format'); return false; }
    return true;
  };

  const handleSweep = async (deposit) => {
    if (!validateSweep()) return;
    try {
      setSweepingId(deposit._id);
      toast.info('Confirm the transaction in your wallet…');

      // wagmi/viem sends eth_sendTransaction straight to the wallet — the
      // MetaMask popup opens without a separate client-side gas estimation.
      const hash = await writeContractAsync({
        address: CONTRACT_ADDRESS,
        abi: SWEEP_ABI,
        functionName: 'sweep',
        args: [deposit.salt, USDT_ADDRESS, toAddress.trim()],
      });
      toast.success(
        <span>Sweep submitted! <a href={`https://bscscan.com/tx/${hash}`} target="_blank" rel="noopener noreferrer" className="underline">View on BscScan</a></span>
      );

      const receipt = await publicClient.waitForTransactionReceipt({ hash });
      if (receipt.status !== 'success') {
        toast.error('Transaction reverted on-chain');
        return;
      }

      await axiosInstance.post('/api/admin/sweep/record', {
        depositIds: [{ depositId: deposit.tranx_id, txHash: hash }],
      }, { headers: { token } });
      toast.success('Sweep confirmed & recorded!');
      setTimeout(async () => {
        const rows = await fetchPending(true);
        await fetchBalances(rows);
      }, 2000);
    } catch (err) {
      toast.error(parseWeb3Error(err));
    } finally {
      setSweepingId(null);
    }
  };

  const handleBatchSweep = async () => {
    if (!validateSweep()) return;
    if (selectedIds.length === 0) { toast.error('Please select at least one deposit'); return; }

    const selected = data.filter(d => selectedIds.includes(d._id));

    // Contract skips zero-balance addresses silently — filter client-side so we
    // know which deposits actually swept (for backend recording).
    const sweepable = selected.filter(d => {
      const bal = balances[d._id];
      return bal !== undefined && bal !== null && bal > 0;
    });

    if (sweepable.length === 0) {
      toast.error('Selected deposits have no on-chain balance. Click Refresh and try again.');
      return;
    }
    if (sweepable.length < selected.length) {
      const skipped = selected.length - sweepable.length;
      toast.info(`Skipping ${skipped} deposit${skipped > 1 ? 's' : ''} with zero on-chain balance.`);
    }

    const salts = sweepable.map(d => d.salt);

    try {
      setIsBatchSweeping(true);
      toast.info('Confirm the transaction in your wallet…');

      const hash = await writeContractAsync({
        address: CONTRACT_ADDRESS,
        abi: SWEEP_ABI,
        functionName: 'batchSweep',
        args: [salts, USDT_ADDRESS, toAddress.trim()],
      });
      toast.success(
        <span>Batch sweep submitted ({sweepable.length} deposits)! <a href={`https://bscscan.com/tx/${hash}`} target="_blank" rel="noopener noreferrer" className="underline">View on BscScan</a></span>
      );
      setSelectedIds([]);

      const receipt = await publicClient.waitForTransactionReceipt({ hash });
      if (receipt.status !== 'success') {
        toast.error('Transaction reverted on-chain');
        return;
      }

      // Decode Swept events to learn which deposits actually swept on-chain.
      const sweptLogs = parseEventLogs({
        abi: SWEEP_ABI,
        eventName: 'Swept',
        logs: receipt.logs,
      }).filter(l => l.address.toLowerCase() === CONTRACT_ADDRESS.toLowerCase());
      const sweptSalts = new Set(sweptLogs.map(l => String(l.args.salt).toLowerCase()));

      const actuallySwept = sweepable.filter(d => sweptSalts.has(String(d.salt).toLowerCase()));

      if (actuallySwept.length === 0) {
        toast.warning('Transaction confirmed but no deposits were actually swept on-chain.');
      } else {
        await axiosInstance.post('/api/admin/sweep/record', {
          depositIds: actuallySwept.map(d => ({ depositId: d.tranx_id, txHash: hash })),
        }, { headers: { token } });
        toast.success(`Batch sweep recorded! ${actuallySwept.length} of ${sweepable.length} deposits swept.`);
      }

      setTimeout(async () => {
        const rows = await fetchPending(true);
        await fetchBalances(rows);
      }, 2000);
    } catch (err) {
      toast.error(parseWeb3Error(err));
    } finally {
      setIsBatchSweeping(false);
    }
  };

  const allSelected = data.length > 0 && data.every(d => selectedIds.includes(d._id));
  const toggleAll = () => setSelectedIds(allSelected ? [] : data.map(d => d._id));
  const toggleOne = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const applyFilters = () => setFilters({ ...tempFilters, page: 1 });
  const clearFilters = () => { setFilters(defaultFilters); setTempFilters(defaultFilters); };
  const handlePageChange = (p) => { setFilters(prev => ({ ...prev, page: p })); setTempFilters(prev => ({ ...prev, page: p })); };

  const handleManualRefresh = async () => {
    const rows = await fetchPending();
    await fetchBalances(rows);
  };

  return (
    <div>
      {/* Wallet + Config */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-start gap-6">
          <div className="flex-shrink-0">
            <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-2">Owner Wallet</p>
            <ConnectButton showBalance={false} chainStatus="icon" />
            {isConnected && (
              <p className={`mt-2 text-xs font-medium ${isOwner ? 'text-emerald-400' : 'text-red-400'}`}>
                {isOwner ? '✓ Contract owner verified' : '✗ Not the contract owner — sweeps will revert'}
              </p>
            )}
          </div>
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">Token Address (USDT BSC)</label>
              <input
                type="text"
                value={USDT_ADDRESS}
                readOnly
                className="w-full rounded-md border border-border/70 bg-white/[0.02] px-3 py-2 text-sm font-mono text-muted-foreground cursor-not-allowed"
              />
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">Destination Address</label>
              <input
                type="text"
                value={toAddress}
                onChange={e => setToAddress(e.target.value)}
                placeholder="0x... (where swept USDT will go)"
                className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground font-mono placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="rounded-lg border border-border/70 bg-card p-5">
          <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-2">Total Pending Deposits</p>
          <p className="text-3xl font-bold text-foreground">{loading ? '—' : stats.totalDeposits}</p>
        </div>
        <div className="rounded-lg border border-border/70 bg-card p-5">
          <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-2">Total On-Chain Balance</p>
          <p className="text-3xl font-bold text-ginox-green">{loading ? '—' : Number(stats.totalUSDT).toFixed(2)}</p>
        </div>
        <div className="rounded-lg border border-border/70 bg-card p-5">
          <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-2">Total On-Chain Balance (Current Page)</p>
          <p className="text-3xl font-bold text-emerald-400">
            {loadingBalances ? '...' : totalOnChain.toFixed(4)}{' '}
            <span className="text-base font-normal text-muted-foreground">USDT</span>
          </p>
        </div>
      </div>

      {/* Batch action bar */}
      {selectedIds.length > 0 && (
        <div className="flex items-center justify-between rounded-lg border border-ginox-green/30 bg-ginox-green/5 px-5 py-3 mb-4">
          <span className="text-sm font-medium text-ginox-green">
            {selectedIds.length} deposit{selectedIds.length > 1 ? 's' : ''} selected
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedIds([])}
              className="rounded-md border border-border/70 bg-white/[0.02] px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
            >
              Clear
            </button>
            <button
              onClick={handleBatchSweep}
              disabled={isBatchSweeping || !isConnected || !isOwner}
              className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-1.5 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isBatchSweeping ? 'Sweeping...' : `Batch Sweep (${selectedIds.length})`}
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">Username / WalletAddress</label>
            <input
              type="text"
              value={tempFilters.username}
              onChange={e => setTempFilters(p => ({ ...p, username: e.target.value }))}
              placeholder="Search…"
              className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            />
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">Start Date</label>
            <input
              type="date"
              value={tempFilters.startDate}
              onChange={e => setTempFilters(p => ({ ...p, startDate: e.target.value }))}
              className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            />
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">End Date</label>
            <input
              type="date"
              value={tempFilters.endDate}
              onChange={e => setTempFilters(p => ({ ...p, endDate: e.target.value }))}
              className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            />
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">Page Size</label>
            <select
              value={tempFilters.pageSize}
              onChange={e => setTempFilters(p => ({ ...p, pageSize: parseInt(e.target.value) }))}
              className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            >
              {[10, 15, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <button onClick={applyFilters} className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors">
            Apply Filters
          </button>
          <button onClick={clearFilters} className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors">
            Clear Filters
          </button>
          <button onClick={handleManualRefresh} className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors ml-auto">
            Refresh
          </button>
          <span className="text-xs text-muted-foreground/60">Auto-refresh every 30s</span>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        {loading ? (
          <p className="py-12 text-center text-sm text-muted-foreground">Loading...</p>
        ) : data.length === 0 ? (
          <p className="py-12 text-center text-sm text-muted-foreground">No pending deposits found</p>
        ) : (
          <>
            <table className="w-full border-separate border-spacing-0">
              <thead>
                <tr>
                  <th className="border-b border-border/40 px-5 py-3.5 text-left">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      className="rounded border-border/70 cursor-pointer accent-ginox-green"
                    />
                  </th>
                  {['Username', 'Tranx ID', 'Deposit Address', 'Amount Received', 'On-Chain Balance', 'Expires At', 'Action'].map(h => (
                    <th key={h} className="border-b border-border/40 px-5 py-3.5 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map(row => (
                  <tr key={row._id} className={`group hover:bg-white/[0.02] transition-colors ${selectedIds.includes(row._id) ? 'bg-ginox-green/5' : ''}`}>
                    <td className="border-b border-border/40 px-5 py-3.5">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(row._id)}
                        onChange={() => toggleOne(row._id)}
                        className="rounded border-border/70 cursor-pointer accent-ginox-green"
                      />
                    </td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{row.username}</td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono text-xs">{row.tranx_id}</td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm">
                      <a
                        href={`https://bscscan.com/address/${row.toWalletAddress}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={row.toWalletAddress}
                        className="font-mono text-xs text-ginox-green hover:underline"
                      >
                        {row.toWalletAddress ? `${row.toWalletAddress.slice(0, 8)}...${row.toWalletAddress.slice(-6)}` : '—'}
                      </a>
                    </td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{row.amountReceived} USDT</td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm">
                      {balances[row._id] === null ? (
                        <span className="text-red-400 text-xs font-medium">Failed to load</span>
                      ) : balances[row._id] !== undefined ? (
                        <span className={`font-mono font-semibold text-sm ${balances[row._id] > 0 ? 'text-emerald-400' : 'text-muted-foreground'}`}>
                          {balances[row._id].toFixed(4)} USDT
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-xs">{loadingBalances ? 'Loading...' : '—'}</span>
                      )}
                    </td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">
                      {row.expiresAt ? new Date(row.expiresAt).toLocaleString() : '—'}
                    </td>
                    <td className="border-b border-border/40 px-5 py-3.5">
                      <button
                        onClick={() => handleSweep(row)}
                        disabled={sweepingId === row._id || isBatchSweeping || !isConnected || !isOwner}
                        className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-3 py-1.5 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {sweepingId === row._id ? 'Sweeping...' : 'Sweep'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
      <div className="border-t border-white/[0.08] px-4">
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalRows={pagination.totalRows}
          pageSize={filters.pageSize}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const GUSDDepositSweepInner = () => {
  const { token } = useSelector((state) => state.auth);
  const { can } = usePermissions();
  const [activeTab, setActiveTab] = useState(0);

  return (
    <div>
      <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
        <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
        Finance
      </div>
      <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">
        GUSD Deposit <span className="text-gradient-ginox">Sweep</span>
      </h1>
      <p className="text-sm text-muted-foreground mb-6">View sweep history and execute sweeps directly from your wallet</p>

      {/* Tabs */}
      <div className="flex border-b border-border/70 mb-6">
        {TABS.map((tab, i) => (
          <button
            key={tab}
            onClick={() => setActiveTab(i)}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors focus:outline-none ${
              activeTab === i
                ? 'border-ginox-green text-ginox-green'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === 0 && can('GET:/api/admin/sweep/pending') && <PendingTab token={token} />}
      {activeTab === 1 && can('GET:/api/admin/sweep/history') && <HistoryTab token={token} />}
      {activeTab === 0 && !can('GET:/api/admin/sweep/pending') && (
        <p className="py-12 text-center text-sm text-muted-foreground">You do not have permission to view pending deposits.</p>
      )}
      {activeTab === 1 && !can('GET:/api/admin/sweep/history') && (
        <p className="py-12 text-center text-sm text-muted-foreground">You do not have permission to view sweep history.</p>
      )}
    </div>
  );
};

// Web3Provider (Wagmi + RainbowKit) is scoped to this page only.
const GUSDDepositSweep = () => (
  <Web3Provider>
    <GUSDDepositSweepInner />
  </Web3Provider>
);

export default GUSDDepositSweep;
